import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { AddFriendInput } from './AddFriendInput';
import { ContactImport } from './ContactImport';
import { FriendCard } from './FriendCard';
import { PendingRequest } from './PendingRequest';
import { LeaderboardView } from './LeaderboardView';
import { EmptyState } from '../shared/EmptyState';
import { UserPlus, ChevronDown, ChevronUp, Phone, Users, X, Share2, QrCode, Loader2, Smartphone, MapPin } from 'lucide-react';
import { haptic } from '../../utils/haptics';
import { QRModal } from '../shared/QRModal';
import { FriendsSkeleton } from '../shared/SkeletonScreens';
import { hasSharedContacts, pickContacts, storeContacts } from '../../services/contactImport';
import { Capacitor } from '@capacitor/core';
import { COUNTRY_CODES } from '../../utils/countryCodes';
import { DOWNLOAD_URL } from '../../constants/links';
import { normalizeToE164, hashPhone } from '../../utils/phoneHash';
import { saveLocalPhone } from '../../utils/localPhone';
import { shareText } from '../../utils/nativeShare';
import { supabase } from '../../lib/supabase';

/** Minimum count shown to avoid "0 crew" for early adopters */
const BASE_CREW_FLOOR = 14;

function useBaseCrewCount(baseAirport: string | undefined) {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!baseAirport) return;
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('base_airport', baseAirport)
      .then(({ count: n }) => {
        const real = n ?? 0;
        setCount(Math.max(real, BASE_CREW_FLOOR));
      });
  }, [baseAirport]);
  return count;
}

type TabView = 'friends' | 'leaderboard';

export function FriendsTab() {
  const { loading, currentUser, friends, pendingRequests, overlaps, refreshData } = useApp();
  const baseCrewCount = useBaseCrewCount(currentUser?.baseAirport);
  const [activeTab, setActiveTab] = useState<TabView>(
    () => (sessionStorage.getItem('rostfound-friends-tab') as TabView) || 'friends'
  );
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // Start hidden to avoid flash; only show after async check confirms not shared
  const [promptChecked, setPromptChecked] = useState(
    () => localStorage.getItem('rostfound_contact_prompt_dismissed') === 'true'
  );
  const [promptDismissed, setPromptDismissed] = useState(
    () => localStorage.getItem('rostfound_contact_prompt_dismissed') === 'true'
  );

  // Check async if contacts were shared — only reveal prompt after check completes
  useEffect(() => {
    hasSharedContacts(currentUser.id).then((shared) => {
      if (shared) setPromptDismissed(true);
      setPromptChecked(true);
    }).catch(() => setPromptChecked(true));
  }, [currentUser.id]);

  // Re-prompt: if dismissed but <3 friends and 7+ days have passed, show again
  const [rePrompt, setRePrompt] = useState(false);
  useEffect(() => {
    if (!promptDismissed || !promptChecked) return;
    if (friends.length >= 3) return;
    const dismissedAt = localStorage.getItem('rostfound_contact_prompt_dismissed_at');
    if (!dismissedAt) return;
    const daysSince = (Date.now() - new Date(dismissedAt).getTime()) / 86400000;
    if (daysSince >= 7) {
      hasSharedContacts(currentUser.id).then((shared) => {
        if (!shared) setRePrompt(true);
      }).catch(() => {});
    }
  }, [promptDismissed, promptChecked, friends.length, currentUser.id]);

  // Phone nudge state
  const [phoneNudgeDismissed, setPhoneNudgeDismissed] = useState(
    () => localStorage.getItem('rostfound_phone_nudge_dismissed') === 'true'
  );
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState('971');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSaving, setPhoneSaving] = useState(false);

  const showPhoneNudge = !currentUser.hasPhone && !phoneNudgeDismissed;

  const handleDismissPhoneNudge = useCallback(() => {
    setPhoneNudgeDismissed(true);
    localStorage.setItem('rostfound_phone_nudge_dismissed', 'true');
  }, []);

  const handleOpenPhoneModal = useCallback(() => {
    setPhoneCountryCode('971');
    setPhoneDigits('');
    setPhoneError(null);
    setShowPhoneModal(true);
  }, []);

  const handleSavePhone = useCallback(async () => {
    setPhoneError(null);
    const digits = phoneDigits.replace(/\D/g, '');
    if (!digits || digits.length < 6 || digits.length > 15) {
      setPhoneError('Please enter a valid phone number (6-15 digits).');
      return;
    }
    const phoneValue = `+${phoneCountryCode}${digits}`;
    const e164 = normalizeToE164(phoneValue);
    if (!e164) {
      setPhoneError('Could not validate this number. Please check the format.');
      return;
    }
    setPhoneSaving(true);
    let phoneHash: string;
    try {
      phoneHash = await hashPhone(e164);
    } catch {
      setPhoneError('Failed to process phone number. Please try again.');
      setPhoneSaving(false);
      return;
    }
    saveLocalPhone(currentUser.id, e164);
    const { error: dbError } = await supabase
      .from('profiles')
      .update({ phone_hash: phoneHash })
      .eq('id', currentUser.id);
    if (dbError) {
      setPhoneError(dbError.message);
      setPhoneSaving(false);
      return;
    }
    setPhoneSaving(false);
    setShowPhoneModal(false);
    setPhoneNudgeDismissed(true);
    await refreshData();
  }, [phoneCountryCode, phoneDigits, currentUser.id, refreshData]);

  const isNative = Capacitor.isNativePlatform();
  const showContactPrompt = (promptChecked && !promptDismissed && isNative) || (rePrompt && isNative);

  const [showContactConsent, setShowContactConsent] = useState(false);

  const handleShareContacts = useCallback(async () => {
    const contacts = await pickContacts();
    if (contacts) {
      await storeContacts(currentUser.id, contacts, currentUser.phone || undefined);
      setPromptDismissed(true);
      setShowContactConsent(false);
      await refreshData();
    }
  }, [currentUser.id, currentUser.phone, refreshData]);

  const handleDismissPrompt = useCallback(() => {
    setPromptDismissed(true);
    setRePrompt(false);
    localStorage.setItem('rostfound_contact_prompt_dismissed', 'true');
    localStorage.setItem('rostfound_contact_prompt_dismissed_at', new Date().toISOString());
  }, []);

  if (loading) return <FriendsSkeleton />;

  return (
    <div className="pb-4">
      {/* Segmented control header */}
      <div className="px-3 py-2 border-b border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
        <div className="flex bg-slate-700/50 rounded-lg p-0.5">
          <button
            onClick={() => { haptic(); setActiveTab('friends'); sessionStorage.setItem('rostfound-friends-tab', 'friends'); }}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activeTab === 'friends'
                ? 'bg-slate-600 text-slate-100 shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Friends
          </button>
          <button
            onClick={() => { haptic(); setActiveTab('leaderboard'); sessionStorage.setItem('rostfound-friends-tab', 'leaderboard'); }}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activeTab === 'leaderboard'
                ? 'bg-slate-600 text-slate-100 shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Leaderboard
          </button>
        </div>
      </div>

      {/* Leaderboard view */}
      {activeTab === 'leaderboard' && <LeaderboardView />}

      {/* Friends view */}
      {activeTab === 'friends' && <>

      {/* Contact sharing prompt — initial + 7-day re-prompt for <3 friends */}
      {showContactPrompt && (
        <div className="mx-3 mt-2.5 p-3 bg-accent/10 border border-accent/30 rounded-xl">
          <div className="flex items-start gap-2.5">
            <Users size={18} className="text-accent shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-100">
                {rePrompt
                  ? `You have ${friends.length} friend${friends.length !== 1 ? 's' : ''} — find more in contacts`
                  : 'Find crew members in your contacts'}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                We'll match your contacts to see who's already on Rost & Found
              </p>
              <div className="flex gap-2 mt-2.5">
                <button
                  onClick={() => setShowContactConsent(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent-dark transition-colors"
                >
                  <Phone size={12} />
                  Share Contacts
                </button>
                <button
                  onClick={handleDismissPrompt}
                  className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Not Now
                </button>
              </div>
            </div>
            <button onClick={handleDismissPrompt} className="text-slate-600 hover:text-slate-400 shrink-0">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Phone nudge banner — shown when user has no phone set */}
      {showPhoneNudge && (
        <div className="mx-3 mt-2.5 p-3 bg-accent/10 border border-accent/30 rounded-xl">
          <div className="flex items-start gap-2.5">
            <Smartphone size={18} className="text-accent shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-100">Find crew faster</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Add your phone number so colleagues can find you. Your number is hashed on-device — only the hash is stored.
              </p>
              <div className="flex gap-2 mt-2.5">
                <button
                  onClick={handleOpenPhoneModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent-dark transition-colors"
                >
                  <Phone size={12} />
                  Add Phone
                </button>
                <button
                  onClick={handleDismissPhoneNudge}
                  className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Not Now
                </button>
              </div>
            </div>
            <button onClick={handleDismissPhoneNudge} className="text-slate-600 hover:text-slate-400 shrink-0">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Add friend */}
      <div className="px-3 pt-2.5 relative">
        <AddFriendInput />
      </div>

      {/* Invite Crew + QR */}
      <div className="px-3 pt-2 flex gap-2">
        <button
          onClick={async () => {
            haptic();
            const text = `Join me on Rost & Found! Use my code "${currentUser.handle}" when you sign up and we both benefit.\n\nDownload: ${DOWNLOAD_URL}\nhttps://rostandfound.app/?ref=${currentUser.handle}`;
            await shareText({ title: 'Rost & Found', text });
          }}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent/10 border border-accent/30 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
        >
          <Share2 size={14} />
          Invite Crew
        </button>
        <button
          onClick={() => { haptic(); setShowQR(true); }}
          className="flex items-center justify-center px-3 py-2.5 rounded-xl bg-accent/10 border border-accent/30 text-accent hover:bg-accent/20 transition-colors"
        >
          <QrCode size={18} />
        </button>
      </div>

      <QRModal
        handle={currentUser.handle}
        name={currentUser.name}
        open={showQR}
        onClose={() => setShowQR(false)}
      />

      {/* Discover Crew - collapsible contact import section */}
      <div className="px-3 pt-2.5">
        <button
          onClick={() => setDiscoverOpen((prev) => !prev)}
          className="w-full flex items-center justify-between py-2 group"
        >
          <div className="flex items-center gap-2">
            <Phone size={13} className="text-slate-500" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Find friends from contacts
            </span>
          </div>
          {discoverOpen ? (
            <ChevronUp size={14} className="text-slate-500 group-hover:text-slate-300 transition-colors" />
          ) : (
            <ChevronDown size={14} className="text-slate-500 group-hover:text-slate-300 transition-colors" />
          )}
        </button>
        {discoverOpen && (
          <div className="mt-1">
            <ContactImport />
          </div>
        )}
      </div>

      {/* Pending requests */}
      {pendingRequests.length > 0 && (
        <div className="px-3 pt-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Pending Requests
          </h3>
          <div className="flex flex-col gap-2">
            {pendingRequests.map((req) => (
              <PendingRequest key={req.id} friendship={req} />
            ))}
          </div>
        </div>
      )}

      {/* Friend list */}
      <div className="px-4 pt-4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Your Crew ({friends.length})
        </h3>
        {friends.length === 0 ? (
          <EmptyState
            icon={!currentUser.hasPhone ? Smartphone : UserPlus}
            title="No friends yet"
            message={
              !currentUser.hasPhone
                ? 'Add your phone so crew can find you. We hash it on your device — only the hash is stored.'
                : 'Add crew members by their handle or import your contacts above'
            }
            action={
              <div className="flex flex-col items-center gap-2">
                {!currentUser.hasPhone && (
                  <>
                    <button
                      onClick={handleOpenPhoneModal}
                      className="flex items-center gap-2 px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark transition-colors"
                    >
                      <Phone size={14} />
                      Add Phone & Find Friends
                    </button>
                    <p className="text-xs text-slate-600">or search by @handle above</p>
                  </>
                )}
                {baseCrewCount !== null && currentUser.baseAirport && (
                  <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400">
                    <MapPin size={12} className="text-accent" />
                    <span>
                      <span className="font-semibold text-slate-300">{baseCrewCount}</span> crew at{' '}
                      <span className="font-semibold text-slate-300">{currentUser.baseAirport}</span> are on Rost & Found
                    </span>
                  </div>
                )}
              </div>
            }
          />
        ) : (
          <div className="flex flex-col gap-2">
            {friends.map((friend) => (
              <FriendCard key={friend.id} friend={friend} overlaps={overlaps} />
            ))}
          </div>
        )}
      </div>

      </>}

      {/* Contact consent dialog */}
      {showContactConsent && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-slate-800 rounded-2xl p-5 w-full max-w-sm border border-slate-700">
            <h3 className="text-lg font-bold text-slate-100 mb-1">Share Contacts</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              We'll match your contacts against existing users so you can find crew on Rost & Found.
            </p>
            <ul className="mt-3 space-y-1.5 text-xs text-slate-400">
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">&#x2022;</span>
                Phone numbers are <strong className="text-slate-300">hashed on your device</strong> — raw numbers never leave your phone
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">&#x2022;</span>
                Hashes are stored so we can notify you when a contact joins later
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">&#x2022;</span>
                You can delete this data anytime in Settings &gt; Privacy
              </li>
            </ul>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowContactConsent(false)}
                className="flex-1 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleShareContacts}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-dark transition-colors"
              >
                <Phone size={14} />
                Continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Phone edit modal */}
      {showPhoneModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-slate-800 rounded-2xl p-5 w-full max-w-sm border border-slate-700">
            <h3 className="text-lg font-bold text-slate-100 mb-1">Add Phone Number</h3>
            <p className="text-xs text-slate-400 mb-3">
              Your number is hashed on-device. Only the hash is sent to the server for contact matching.
            </p>
            {phoneError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-xs mb-3">{phoneError}</div>
            )}
            <div className="flex gap-2">
              <div className="relative shrink-0">
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  className="w-[100px] px-2.5 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent appearance-none"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.dial} className="bg-slate-700 text-slate-100">
                      {c.flag} +{c.dial}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
                />
              </div>
              <input
                type="tel"
                autoComplete="tel-national"
                value={phoneDigits}
                onChange={(e) => setPhoneDigits(e.target.value.replace(/[^\d\s]/g, ''))}
                placeholder="50 1234567"
                className="flex-1 px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowPhoneModal(false)}
                disabled={phoneSaving}
                className="flex-1 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePhone}
                disabled={phoneSaving}
                className="flex-1 py-2.5 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {phoneSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                {phoneSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
